import { Logo } from "@/components/shared/Logo";
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
    useSidebar,
} from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { NavLink, useLocation } from "react-router";
import {
    ADMIN_SECTIONS,
    ROUTES_BY_SECTION,
    type AdminRoute,
    type AdminSection,
} from "./admin-routes";

function LogoMark() {
    return (
        <div className="h-6 w-6 overflow-hidden rounded-sm">
            <img src="/favicon.ico" alt="" />
        </div>
    );
}

function SidebarLogo() {
    const { state } = useSidebar();
    const isMobile = useIsMobile();
    const collapsed = state === "collapsed";

    return (
        <SidebarHeader className="px-3 py-2">
            <div className="flex items-center gap-2.5 overflow-hidden">
                <LogoMark />
                {!collapsed && (
                    <div className="flex min-w-0 flex-col gap-0">
                        <Logo
                            style={{ width: isMobile ? 70 : 120 }}
                            className="brightness-0 invert"
                        />
                        <span className="text-sidebar-primary text-[10px] font-medium tracking-[0.08em]">
                            Admin
                        </span>
                    </div>
                )}
            </div>
        </SidebarHeader>
    );
}

function NavItem({ route }: { route: AdminRoute }) {
    const { pathname } = useLocation();
    const isActive =
        route.path === "home"
            ? pathname === "/admin/home" || pathname === "/admin"
            : pathname.startsWith(`/admin/${route.path}`);

    return (
        <SidebarMenuItem>
            <SidebarMenuButton
                isActive={isActive}
                tooltip={route.name}
                className="relative rounded-xs transition-colors duration-150"
                render={<NavLink to={`/admin/${route.path}`} />}
            >
                <route.icon className="size-4 shrink-0" />
                <span className="truncate">{route.name}</span>
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
}

function NavSection({
    section,
    isFirst,
}: {
    section: AdminSection;
    isFirst: boolean;
}) {
    const routes = ROUTES_BY_SECTION.get(section) ?? [];
    if (routes.length === 0) return null;

    return (
        <SidebarGroup>
            {!isFirst && (
                <SidebarGroupLabel className="text-sidebar-foreground/40 mb-0.5 px-3 text-[10px] font-semibold tracking-widest uppercase">
                    {section}
                </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
                <SidebarMenu>
                    {routes.map((route) => (
                        <NavItem key={route.path} route={route} />
                    ))}
                </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
    );
}

export function AdminSidebar() {
    return (
        <Sidebar collapsible="icon">
            <SidebarLogo />
            <SidebarContent>
                {ADMIN_SECTIONS.map((section, i) => (
                    <NavSection
                        key={section}
                        section={section}
                        isFirst={i === 0}
                    />
                ))}
            </SidebarContent>
            <SidebarRail />
        </Sidebar>
    );
}
